import React, { useEffect, useState } from 'react'
import styled from 'styled-components';
import StyledText from '../../components/StyledComponents/StyledText';
import { useTheme } from '../../middleware/ThemeContext';
import CustomInput, { ErrorText } from '../../components/form/CustomInput';
import { FieldLabel } from '../../components/FormBuilder/FieldSettings/FieldSettingsModal';
import TopImg from '../../assets/images/popupImg.png';
import TopImgDark from '../../assets/images/popupImg-dark.png';
import ToggleSwitch from '../../components/StyledComponents/StyledToggle';
import { CorporateTheme, Theme } from '../../utils/themes';
import CustomTextArea from '../../components/form/CustomTextArea';
import { Button } from '../../components/form/Button';
import CrossIcon from '../../assets/icons/CrossIcon';
import { timeZones } from '../../utils/timeZones';
import CustomInputURL from '../../components/form/CustomInputURL';
import AMSSelect from '../../components/AMSSelect';
import { useNavigate } from 'react-router-dom';
import { stat } from 'fs';
import { WebsiteFormValues } from '../../types/types';
import { useCreateWebsiteMutation, useDnsRetryMutation, useGetDnsSetupQuery, useGetDnsVerificationQuery, useUpdateSettingDetailsMutation } from '../../api/pageBuilderApiSlice';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useToast } from '../../components/toast/ToastContext';
import UpdateFileIcon from '../../assets/icons/UpdateFileIcon';
import SaveIcon from '../../assets/icons/SaveIcon';
import NewCopyIcon from '../../assets/icons/NewCopyIcon';
import { copyToClipboard } from '../../utils/helper';
import CheckIcon from '../../components/StyledComponents/CheckBox/CheckIcon';
import { isNull } from 'lodash';

interface popupProps {
    onClose: (shouldNavigateBack?: boolean) => void;
    setWebsiteDetails: (value: React.SetStateAction<WebsiteFormValues>) => void;
    websiteDetails: WebsiteFormValues;
    isFromEdit?: boolean
}
type FormErrors = {
    websiteName?: string;
    websiteURl?: string;
    timezone?: string;
};

const getValidationErrors = (values: WebsiteFormValues): FormErrors => {
    const newErrors: FormErrors = {};

    const websiteName = values.websiteName?.trim() || '';
    const websiteURL = values.websiteURl?.trim() || '';

    if (!websiteName) {
        newErrors.websiteName = 'Website name is required';
    }

    if (!websiteURL) {
        newErrors.websiteURl = 'Website URL is required';
    } else {
        let clean = websiteURL.toLowerCase();
        clean = clean.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0];
        const isValidDomain =
            clean.includes('.') &&
            clean.length > 3 &&
            !clean.startsWith('.') &&
            !clean.endsWith('.');
        const isValidName = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/i.test(clean);

        if (!isValidDomain && !isValidName) {
            newErrors.websiteURl = 'Enter a valid domain (e.g., "abc.f10.com") or name (e.g., "mernitsolutions")';
        }
    }

    if (!values.timezone) {
        newErrors.timezone = 'Timezone is required';
    }

    return newErrors;
};

const toSlug = (value: string) =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');

const toWebsiteDomain = (value: string, subdomain?: boolean) => {
    const slug = toSlug(value);
    if (subdomain) {
        return slug ? `${slug}` : '';
    }
    return slug ? `${slug}.com` : '';
};

const normalizeWebsiteHost = (value: string) =>
    value
        .trim()
        .toLowerCase()
        .replace(/^(https?:\/\/)?(www\.)?/, '')
        .split('/')[0];

const isCustomDomainValue = (value: string) => {
    const host = normalizeWebsiteHost(value);
    const customDomainRegex = /^(?!-)(?:[a-z0-9-]{1,63}\.)+[a-z]{2,63}$/i;
    return customDomainRegex.test(host);
};

const DNS_CONFIGURATION_EXCLUDED_HOSTS = ['f10solutions.co.uk'];

const isDnsConfigurationExcludedHost = (host: string) =>
    DNS_CONFIGURATION_EXCLUDED_HOSTS.some(
        (domain) => host === domain || host.endsWith(`.${domain}`)
    );

const WebsiteConfigPopup: React.FC<popupProps> = ({ onClose, setWebsiteDetails, websiteDetails, isFromEdit }) => {
    const { theme } = useTheme();
    const showToast = useToast();
    const isDarking = localStorage.getItem('appTheme') === 'dark';
    const navigate = useNavigate(); const timeZoneOptions = timeZones;
    const [draftDetails, setDraftDetails] = useState<WebsiteFormValues>(websiteDetails);
    const initialDetailsRef = React.useRef<WebsiteFormValues>(websiteDetails);
    const [createWebsite, { isLoading }] = useCreateWebsiteMutation();
    const [updateSettingDetails, { isLoading: isUpdating }] = useUpdateSettingDetailsMutation();
    const { data: dnsSetupData, isLoading: isGettingDnsSetup } = useGetDnsSetupQuery();
    const { data: dnsVerificationData, isLoading: isVerifyingDns, refetch: verifyDns } = useGetDnsVerificationQuery();
    const [dnsRetry, { isLoading: isRetryingDns }] = useDnsRetryMutation();

    React.useEffect(() => {
        setDraftDetails(websiteDetails);
        initialDetailsRef.current = websiteDetails;
    }, [websiteDetails]);

    const errors = React.useMemo(() => getValidationErrors(draftDetails), [draftDetails]);

    const isFormInvalid =
        !draftDetails.websiteName?.trim() ||
        // !isValidDomain(draftDetails.websiteURl) ||
        !draftDetails.timezone;

    const hasValidationErrors = Boolean(
        errors.websiteName || errors.websiteURl || errors.timezone
    );

    const normalizedWebsiteDomain = React.useMemo(
        () => normalizeWebsiteHost(draftDetails.websiteURl || ''),
        [draftDetails.websiteURl]
    );

    const shouldShowDnsConfiguration = React.useMemo(() => {
        const host = normalizeWebsiteHost(draftDetails.websiteURl || '');
        return isCustomDomainValue(host) && !isDnsConfigurationExcludedHost(host);
    }, [draftDetails.websiteURl]);

    const handleCopyDnsField = async (value: string, label: string) => {
        if (!value) return;
        await copyToClipboard(value);
        showToast('Success', `${label} copied to clipboard`, 'success');
    };

    const handleCreate = async () => {
        if (!validateForm()) return;
        try {
            if (!draftDetails) return;

            const payload = {
                name: draftDetails.websiteName,
                webDomain: draftDetails.websiteURl.trim(),
                timeZone: draftDetails.timezone,
                welcomeNote: draftDetails.loginHeading ?? '',
                description: draftDetails.loginDescription ?? '',
                settings: {
                    metaData: {
                        colorSettings: {
                            primaryColor: draftDetails.primaryColor,
                            secondaryColor: draftDetails.secondaryColor,
                            textColor: draftDetails.textColor
                        },
                        cookiesConsentBanner: {
                            enabled: true
                        },
                        font: {
                            primaryFont: draftDetails.fontFamily
                        }
                    }
                }
            };


            await createWebsite(payload).unwrap();
            setWebsiteDetails(draftDetails);
            localStorage.removeItem('activeTabId');
            navigate('/home/builder');
        } catch (error: any) {
            // ✅ centralized error handling
            const message =
                error?.data?.message ||
                error?.error ||
                'Something went wrong while creating website';

        }

    };

    const handleUpdateDetails = async () => {
        const payload = {
            name: draftDetails?.websiteName,
            webDomain: draftDetails?.websiteURl?.trim(),
            timeZone: draftDetails?.timezone,
            welcomeNote: draftDetails?.loginHeading,
            description: draftDetails?.loginDescription,
            settings: {
                metaData: {
                    customScreen: draftDetails?.active
                }
            }
        };

        try {
            await updateSettingDetails(payload).unwrap();
            setWebsiteDetails(draftDetails);
        } catch (error: any) {
            // ✅ centralized error handling
            const message =
                error?.data?.message ||
                error?.error ||
                'Something went wrong while updating website';
            showToast('Error', message, 'error');
        }
        finally {
            onClose();
        }
    };



    const validateForm = () => {
        const newErrors = getValidationErrors(draftDetails);
        return Object.keys(newErrors).length === 0;
    };

    const handleConfigure = () => {
        if (!validateForm()) return;
        setWebsiteDetails(draftDetails);
        onClose(false); // Configuration complete, just close popup
    };

    const handleCancel = () => {
        setDraftDetails(initialDetailsRef.current);
        setWebsiteDetails(initialDetailsRef.current);
        onClose(true); // User cancelled, navigate back
    };

    const hanldeToggle = () => {
        setDraftDetails({ ...draftDetails, active: !draftDetails.active });
    }

    const handleRetryDns = async () => {
        try {
            await dnsRetry({}).unwrap();
            verifyDns(); // Refetch verification status after retry
        } catch (error: any) {
            // ✅ centralized error handling
            const message =
                error?.data?.message ||
                error?.error ||
                'Something went wrong while creating website';
            showToast('Error', message, 'error');
        }
    };

    const selectedTimeZoneOption = timeZoneOptions.find((tz) => tz.value === draftDetails.timezone) ?? null;

    return (
        <PopUpWrapper>
            <PopUpCon style={{ backgroundColor: theme.colors.contentBackground }}>
                <div className='popHeader'>
                    <span onClick={handleCancel} className='closeIcon' style={{ backgroundColor: theme.CorporateTheme.colors.background }}>
                        <CrossIcon color={theme.CorporateTheme.colors.textSecondary} />
                    </span>
                    <img src={theme.name === 'light' ? TopImg : TopImgDark} alt='Config Popup' width={'428px'} height={'100%'} />
                </div>
                <div className='popForm'>
                    <StyledText
                        htmlTag={'h1'}
                        fontSize='16px'
                        fontWeight={600}
                        textColor={theme.CorporateTheme.colors.primary}
                        className='text-center mb-1'
                    >
                        Let’s Setup Your Website
                    </StyledText>
                    <StyledText
                        htmlTag={'p'}
                        fontSize='14px'
                        fontWeight={500}
                        textColor={theme.CorporateTheme.colors.textSecondary}
                        className='text-center mb-3'
                    >
                        Customize your brand Identity & we will handle the rest.
                    </StyledText>
                    <div className="py-1">
                        <FieldLabel theme={theme}>
                            Website Name <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>*</span>
                        </FieldLabel>
                        <CustomInput
                            type="text"
                            placeholder="Enter widget name here"
                            value={draftDetails.websiteName}
                            onChange={(value: string) => {
                                const generatedUrl = toWebsiteDomain(value, true);
                                setDraftDetails((prev) => ({
                                    ...prev,
                                    websiteName: value,
                                    websiteURl: isFromEdit ? prev.websiteURl : generatedUrl
                                }));
                            }}
                            error={errors.websiteName}

                        />
                    </div>
                    <div className='d-flex py-1 gap-4'>
                        <div className='col'>
                            <FieldLabel theme={theme}>
                                Website Url
                            </FieldLabel>
                            <CustomInputURL
                                value={draftDetails.websiteURl}
                                onChange={(value) => {
                                    setDraftDetails({ ...draftDetails, websiteURl: value });
                                }}
                                hasError={errors.websiteURl}
                                validationType='domain-or-name'
                                disabled={true}
                            />
                        </div>
                        <div className='col'>
                            <FieldLabel theme={theme}>
                                Timezone
                            </FieldLabel>
                            <AMSSelect
                                placeholder='Select Timezone'
                                staticOptions={timeZoneOptions}
                                value={selectedTimeZoneOption}
                                // onChange={(option) => setTheTimeZone(option ? option.value : null)}
                                // onChange={(option) => setState({ ...state, timeZone: option ? option.value : null })}
                                onChange={(option) => {
                                    setDraftDetails({ ...draftDetails, timezone: option ? option.value : "" });
                                }}
                                isSearchable
                                showIcons
                                error={errors.timezone}
                            />
                        </div>
                    </div>
                    {/* {shouldShowDnsConfiguration && (
                        <div className='dnsConfiguration'>
                            <div className='d-flex align-items-center gap-2' >
                                <StyledText
                                    htmlTag={'h3'}
                                    fontSize='14px'
                                    fontWeight={600}
                                    textColor={theme.CorporateTheme.colors.primary}
                                >
                                    DNS Configuration
                                </StyledText>
                                {dnsVerificationData?.verified ? <div style={{ background: CorporateTheme.colors.green, borderRadius: '50%', padding: 5, height: 20, width: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CheckIcon color={CorporateTheme.colors.white} size='13' /></div> : null}
                            </div>
                            <StyledText
                                htmlTag={'p'}
                                fontSize='12px'
                                fontWeight={500}
                                textColor={theme.CorporateTheme.colors.textSecondary}
                                className='dnsSubText'
                            >
                                Add the following DNS records to your domain provider. This will point your domain to your career site.
                            </StyledText>
                            <div style={{
                                padding: 20,
                                backgroundColor: theme.CorporateTheme.colors.lightBlueHeaderBg,
                                border: `1px solid ${theme.CorporateTheme.colors.border}`,
                                borderRadius: '6px',
                                marginTop: 15
                            }}>
                                <div className='dnsRecordHeader'>
                                    <StyledText
                                        fontSize='13px'
                                        fontWeight={600}
                                        textColor={theme.CorporateTheme.colors.primary}
                                    >
                                        CNAME Record
                                    </StyledText>
                                    <StyledText
                                        fontSize='12px'
                                        fontWeight={500}
                                        textColor={theme.CorporateTheme.colors.textSecondary}
                                    >
                                        Points your domain to our servers
                                    </StyledText>
                                </div>
                                <div className='dnsRecordRow dnsRecordLabelRow'>
                                    <StyledText
                                        fontSize='12px'
                                        fontWeight={600}
                                        textColor={theme.CorporateTheme.colors.headerColor}
                                    >
                                        NAME
                                    </StyledText>
                                    <StyledText
                                        fontSize='12px'
                                        fontWeight={600}
                                        textColor={theme.CorporateTheme.colors.headerColor}
                                    >
                                        VALUE
                                    </StyledText>
                                </div>
                                <div className='dnsRecordRow'>
                                    <ValueInput theme={theme}>
                                        <StyledText
                                            fontSize='12px'
                                            fontWeight={600}
                                            textColor={theme.CorporateTheme.colors.headerColor}
                                        >
                                            CNAME
                                        </StyledText>
                                        <button
                                            type='button'
                                            className='dnsCopyButton'
                                            aria-label='Copy CNAME record name'
                                            onClick={() => handleCopyDnsField('CNAME', 'CNAME')}
                                        >
                                            <NewCopyIcon
                                                size={14}
                                                color={theme.CorporateTheme.colors.textSecondary}
                                            />
                                        </button>
                                    </ValueInput>
                                    <ValueInput theme={theme}>
                                        <StyledText
                                            fontSize='12px'
                                            fontWeight={600}
                                            textColor={theme.CorporateTheme.colors.headerColor}
                                        >
                                            {dnsSetupData?.cnameTarget}
                                        </StyledText>
                                        <button
                                            type='button'
                                            className='dnsCopyButton'
                                            aria-label='Copy CNAME record value'
                                            onClick={() => handleCopyDnsField(normalizedWebsiteDomain, 'CNAME value')}
                                        >
                                            <NewCopyIcon
                                                size={14}
                                                color={theme.CorporateTheme.colors.textSecondary}
                                            />
                                        </button>
                                    </ValueInput>

                                </div>
                                <div className='d-flex align-items-center justify-content-end mt-3' >
                                    {dnsVerificationData?.verified ? null : <Button
                                        theme={theme}
                                        onClick={handleRetryDns}
                                        height='35px'
                                        fontSize='14px'
                                        disabled={isGettingDnsSetup || isVerifyingDns || isRetryingDns || dnsVerificationData?.verified}>
                                        {isRetryingDns ? <LoadingSpinner size='14px' color={theme.CorporateTheme.colors.background} /> : null}
                                        Verify
                                    </Button>}
                                </div>
                            </div>

                        </div>
                    )} */}
                    <div className="py-1">
                        <FieldLabel theme={theme}>
                            Welcome Heading
                        </FieldLabel>
                        <CustomInput
                            type="text"
                            placeholder="Enter welcome heading"
                            value={draftDetails.loginHeading}
                            onChange={(value: string) => {
                                setDraftDetails({ ...draftDetails, loginHeading: value })
                            }}
                        // error={componentNameError ?? undefined}
                        />
                    </div>
                    <div className="py-1">
                        <FieldLabel theme={theme}>
                            Description
                        </FieldLabel>
                        <CustomTextArea
                            type="textarea"
                            placeholder="Enter description..."
                            value={draftDetails.loginDescription}
                            onChange={(value: string) => {
                                setDraftDetails({ ...draftDetails, loginDescription: value })
                            }}
                        />
                    </div>
                </div>
                {isFromEdit ?
                    <div className='d-flex justify-content-end gap-2 pe-4 pt-2 py-3'>
                        <Button
                            theme={theme}
                            btnBg={theme?.CorporateTheme?.colors?.filterBG}
                            color={theme?.CorporateTheme?.colors?.textSecondary}
                            type="button"
                            $variant="customButton"
                            className="px-3"
                            onClick={handleCancel}
                            disabled={isUpdating}
                        >
                            <CrossIcon />
                            Cancel
                        </Button>
                        <Button
                            theme={theme}
                            btnBg={theme?.CorporateTheme?.colors?.primary}
                            color={theme?.CorporateTheme?.colors?.background}
                            type="button"
                            $variant="customButton"
                            className={`px-3`}
                            onClick={handleUpdateDetails}

                            disabled={isUpdating || isFormInvalid || hasValidationErrors}
                        >
                            {isUpdating ? <LoadingSpinner size='16px' color={theme?.CorporateTheme?.colors?.background} /> : <SaveIcon color={theme.CorporateTheme.colors.background} />}
                            Update
                        </Button>
                    </div>
                    : <div className='popFooter' style={{ backgroundColor: theme.CorporateTheme.colors.darkLightBG }}>
                        <Button
                            theme={theme}
                            $variant='customButton'
                            borderColor={theme.CorporateTheme.colors.border}
                            btnBg={theme.CorporateTheme.colors.background}
                            color={theme.CorporateTheme.colors.headerColor}
                            disabled={isFormInvalid || hasValidationErrors}
                            // width='228px'
                            // onClick={() => navigate('/home/builder')}

                            onClick={handleCreate}
                        >
                            {isLoading ? <LoadingSpinner theme={theme} size='20px' color={theme.CreateFormTheme.colors.headerColor} /> : 'Continue With Default Theme'}

                        </Button>
                        <Button
                            theme={theme}
                            // $variant='customize'
                            // width='163px'
                            onClick={handleConfigure}
                            fontSize='14px'
                            disabled={isFormInvalid || hasValidationErrors}
                        >
                            Configure Branding
                        </Button>
                    </div>}
            </PopUpCon>
        </PopUpWrapper>
    )
}

export default WebsiteConfigPopup

const PopUpWrapper = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgb(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    z-index: 9999;
`;


const ValueInput = styled.div<{ theme: Theme }>`
        background: ${({ theme }) => theme?.CorporateTheme?.colors?.background || '#ffffff'};
        border: 1px solid ${({ theme }) => theme?.CorporateTheme?.colors?.border || '#e5e7eb'};
        border-radius: 6px;
        padding: 8px 10px;
        min-height: 36px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: ${({ theme }) => theme?.CorporateTheme?.colors?.primary};
        font-size: 13px;
        font-weight: 500;
        word-break: break-all;
    
`
const PopUpCon = styled.div`
    width: 800px;
    margin: 0 auto;
    border-radius: 5px;
    height: auto;
    max-height: calc(100vh - 120px);
    overflow-y: auto;

    & .popHeader{
        position: relative;
        text-align: center;
        margin: 8px;
        border-radius: 8px;
        padding-top: 40px;
        background: linear-gradient(180deg, rgba(0, 80, 255, 0.1) 0%, rgba(0, 80, 255, 0.1) 50%, rgba(0, 80, 255, 0.1) 100%);

        & .closeIcon{
            position: absolute;
            top: 10px;
            right: 10px;
            width: 32px;
            height: 32px;
            border-radius: 50px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
        }
    }
    & .popForm{
        padding: 20px 30px;
    }

    & .dnsConfiguration{
        margin-top: 20px;
        margin-bottom: 12px;

        & .dnsSubText{
            margin-top: 4px;
            margin-bottom: 10px;
        }
    }

    & .dnsRecordCard{
        border-radius: 8px;
        background: ${props => props.theme?.CorporateTheme?.colors?.primary};
        border: 1px solid ${props => props.theme?.CorporateTheme?.colors?.border};
        padding: 10px;
    }

    & .dnsRecordHeader{
        margin-bottom: 8px;
    }

    & .dnsRecordRow{
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
    }

    & .dnsRecordLabelRow{
        margin-bottom: 6px;
        margin-top:15px;

        & span{
            font-size: 11px;
            font-weight: 700;
            color: ${props => props.theme?.CorporateTheme?.colors?.textSecondary};
            letter-spacing: 0.02em;
        }
    }

    

    & .dnsValueText{
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        padding-right: 8px;
    }

    & .dnsCopyButton{
        border: none;
        background: transparent;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        padding: 2px;
    }

    & .popFooter{
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 15px 30px;
    }
`;